// import React, { useState } from 'react';
import React, { useState, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Avatar, Typography, Box, CircularProgress, IconButton, Tooltip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SettingsIcon from '@mui/icons-material/Settings';
import { styled } from '@mui/material/styles';
import ChatPayment from '../../screens/PaymentSummary/ChatPayment';
import { COLORS, RADIUS } from '../../theme';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UserActionModal = forwardRef (
({ user, sub, onAvatarUpdate }, ref) => {
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const BASE_PATH = process.env.REACT_APP_BASE_PATH;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  useImperativeHandle(ref, () => ({
    openModal: handleOpen,
    closeModal: handleClose
  }));
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      setError('Please select an image file (JPEG, PNG, etc.)');
      return;
    }

    if (file.size > 1024 * 1024) {
      setError('File size should be less than 1MB');
      return;
    }

    setError('');
    setSelectedFile(file);
    handleUpload(file);
  };

  const handleUpload = async (file) => {
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', user.id);

    setUploading(true);
    try {
      const response = await axios.post(`${BASE_URL}/upload-avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        onAvatarUpdate?.(response.data.avatarUrl);
      }
    } catch (err) {
      setError('Failed to upload image. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Tooltip title="User Settings">
  <IconButton
    onClick={handleOpen}
    sx={{
      '& svg': {
        color: '#000000', // default color
        fontSize: 24,
        transition: 'all 0.3s ease',
      },
      '&:hover svg': {
        color: COLORS.gold, // hover color
        fontSize: 30,
      },
    }}
  >
    <SettingsIcon />
  </IconButton>
</Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            borderRadius: RADIUS.modal,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: COLORS.gray,
            color: COLORS.gold,
            fontWeight: 'bold',
            borderTopLeftRadius: RADIUS.card,
            borderTopRightRadius: RADIUS.card,
            padding: 2,
          }}
        >
          User Settings
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            background: `linear-gradient(to bottom, ${COLORS.gray}, ${COLORS.dark})`,
            color: 'white',
            padding: 3,
          }}
        >
          <Box sx={{ textAlign: 'center', marginBottom: 3 }}>
            <Avatar
              src={BASE_PATH + user?.avatar}
              sx={{ width: 80, height: 80, margin: '0 auto 16px' }}
            />
            <Typography variant="h6" sx={{ color: 'white', marginBottom: 2 }}>
              {user?.name}
            </Typography>

            <Button
              component="label"
              variant="contained"
              startIcon={<CloudUploadIcon />}
              disabled={uploading}
            >
              Upload Avatar
              <VisuallyHiddenInput type="file" accept="image/*" onChange={handleFileChange} />
            </Button>

            {uploading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {error && (
              <Typography sx={{ color: 'white', marginTop: 1 }} variant="body2">
                {error}
              </Typography>
            )}

            <Typography variant="body2" sx={{ color: 'white', marginTop: 1 }}>
              Max file size: 1MB (JPEG, PNG)
            </Typography>
          </Box>

          <Box>
            {sub === null ? (
              <ChatPayment />
            ) : (
              <Typography variant="body2" sx={{ color: 'white' }}>
                Subscription ends on: {sub.end_date}
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            backgroundColor: COLORS.gray,
            borderBottomLeftRadius: RADIUS.card,
            borderBottomRightRadius: RADIUS.card,
            padding: 2,
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: '#444' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default UserActionModal;
